import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, apiPost, apiPut } from '../../lib/api'
import { InlineError } from '../InlineError'

// PO header create / copy / edit (docs/BossMultiPO.md §2.8). Legacy POSummaryEditor's fields,
// minus the reminder date (B2: it was written into the lockdown date).

export type PoHeader = {
    poGuid: string; poNumber: string; catalogGuid: string
    shipDate: string | null; lockdownDate: string | null
    discountPercent: number; discountDays: number; termsDays: number; shippingMethod: string
    isGroup: boolean; brandId: string | null; season: string | null; supplierName: string | null; canEdit: boolean
}

export type HeaderDialogMode =
    | { kind: 'new' }
    | { kind: 'copy'; from: string }
    | { kind: 'edit'; poGuid: string }

type Brand = { id: string; name: string }
type Catalog = { catalogGuid: string; season: string | null; itemCount: number }

/** Legacy POSummary.enmShippingMethod, as stored in PO_POs.DeliveryCarrier. */
export const SHIPPING_METHODS: { key: string; label: string }[] = [
    { key: 'NONE_SPECIFIED', label: 'None specified' },
    { key: 'CHEAPEST', label: 'Cheapest' },
    { key: 'GROUND', label: 'Ground' },
    { key: 'LOOMIS', label: 'Loomis' },
    { key: 'OCEAN_FREIGHT', label: 'Ocean freight' },
    { key: 'POST', label: 'Post' },
    { key: 'POST_GROUND_INSURED', label: 'Post ground, insured' },
    { key: 'POST_GROUND_NOT_INSURED', label: 'Post ground, not insured' },
    { key: 'UPS', label: 'UPS' },
]

type Form = {
    poNumber: string; shipDate: string; lockdownDate: string
    discountPercent: string; discountDays: string; termsDays: string; shippingMethod: string
}

const EMPTY: Form = {
    poNumber: '', shipDate: '', lockdownDate: '',
    discountPercent: '0', discountDays: '0', termsDays: '0', shippingMethod: 'NONE_SPECIFIED',
}

const day = (s: string | null) => (s ? s.slice(0, 10) : '')

/** The client-side half of the legacy POSummaryEditor checks; the server repeats all of them. */
export function headerProblem(f: Form, today: string, currentShipDate: string | null): string | null {
    if (!f.poNumber.trim()) return 'Enter a PO number.'
    const pct = Number(f.discountPercent)
    if (f.discountPercent.trim() === '' || !Number.isFinite(pct) || pct < 0 || pct >= 100) return 'The discount must be from 0 to under 100%.'
    // PO_POs.TermsDiscountPercent holds whole numbers only.
    if (!Number.isInteger(pct)) return 'The discount must be a whole percent.'
    for (const v of [f.discountDays, f.termsDays])
        if (!/^\d+$/.test(v.trim())) return 'Terms days must be whole numbers, 0 or more.'
    if (!f.shipDate) return 'Enter a ship date.'
    if (!f.lockdownDate) return 'Enter a lockdown date.'
    if (f.shipDate !== day(currentShipDate) && f.shipDate <= today) return 'The ship date must be later than today.'
    if (f.lockdownDate > f.shipDate) return 'The lockdown date cannot be after the ship date.'
    return null
}

const localToday = () => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function PoHeaderDialog({ token, store, mode, onSaved, onClose }: {
    token: string | null; store: string; mode: HeaderDialogMode
    /** Called with the created or edited PO once the save has landed. */
    onSaved: (poGuid: string) => void
    onClose: () => void
}) {
    const sourceGuid = mode.kind === 'copy' ? mode.from : mode.kind === 'edit' ? mode.poGuid : null
    const headerQuery = useQuery<PoHeader>({
        queryKey: ['multipo-header', store, sourceGuid],
        queryFn: () => api(`/api/purchase-orders/multi/pos/${sourceGuid}?store=${store}`, token),
        enabled: sourceGuid !== null,
        gcTime: 0,
    })
    const header = headerQuery.data

    const [brandId, setBrandId] = useState('')
    const [catalogGuid, setCatalogGuid] = useState('')
    const [form, setForm] = useState<Form>(EMPTY)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const brandsQuery = useQuery<Brand[]>({
        queryKey: ['multipo-brands'],
        queryFn: () => api('/api/purchase-orders/multi/brands', token),
        enabled: mode.kind === 'new',
    })
    const catalogsQuery = useQuery<Catalog[]>({
        queryKey: ['multipo-catalogs', brandId],
        queryFn: () => api(`/api/purchase-orders/multi/catalogs?brandId=${brandId}`, token),
        enabled: mode.kind === 'new' && !!brandId,
    })

    // Copy and edit start from the source PO's header. A copy keeps the terms and shipping but
    // not the number, and only keeps the dates when the ship date is still ahead.
    useEffect(() => {
        if (!header) return
        const future = day(header.shipDate) > localToday()
        setCatalogGuid(header.catalogGuid)
        setForm({
            poNumber: mode.kind === 'edit' ? header.poNumber : '',
            shipDate: mode.kind === 'edit' || future ? day(header.shipDate) : '',
            lockdownDate: mode.kind === 'edit' || future ? day(header.lockdownDate) : '',
            discountPercent: String(header.discountPercent),
            discountDays: String(header.discountDays),
            termsDays: String(header.termsDays),
            shippingMethod: header.shippingMethod,
        })
    }, [header, mode.kind])

    const set = (k: keyof Form) => (e: { target: { value: string } }) => setForm(f => ({ ...f, [k]: e.target.value }))
    const readOnly = mode.kind === 'edit' && header !== undefined && !header.canEdit
    const loading = sourceGuid !== null && headerQuery.isLoading
    const problem = headerProblem(form, localToday(), mode.kind === 'edit' ? header?.shipDate ?? null : null)

    const submit = async () => {
        if (problem || !catalogGuid || busy || readOnly) return
        setBusy(true)
        setError(null)
        const body = {
            store,
            poNumber: form.poNumber.trim(),
            catalogGuid,
            shipDate: form.shipDate,
            lockdownDate: form.lockdownDate,
            discountPercent: Number(form.discountPercent),
            discountDays: Number(form.discountDays),
            termsDays: Number(form.termsDays),
            shippingMethod: form.shippingMethod,
            copyFrom: mode.kind === 'copy' ? mode.from : null,
        }
        try {
            const res: { poGuid: string } = mode.kind === 'edit'
                ? await apiPut(`/api/purchase-orders/multi/pos/${mode.poGuid}`, token, body)
                : await apiPost('/api/purchase-orders/multi/pos', token, body)
            onSaved(res.poGuid)
        } catch (err) {
            setError(errorText(err) ?? 'The PO could not be saved.')
            setBusy(false)
        }
    }

    const title = mode.kind === 'new' ? 'New PO' : mode.kind === 'copy' ? 'Copy PO' : 'Edit PO'
    const catalogLabel = header ? `${header.supplierName ?? 'Catalog'} · ${header.season ?? 'No season'}` : ''

    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 560 }}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="modal-body">
                    {headerQuery.isError && <InlineError message={errorText(headerQuery.error) ?? 'Failed to load the PO.'} />}
                    {loading && <p>Loading the PO…</p>}
                    {!loading && !headerQuery.isError && (
                        <>
                            {mode.kind === 'copy' && header && (
                                <p className="multipo-dialog-note">
                                    Copies the quantities from <strong>{header.poNumber}</strong> into a new Direct-to-Store PO for this store.
                                </p>
                            )}
                            {mode.kind === 'new' && (
                                <p className="multipo-dialog-note">Creates a Direct-to-Store PO for this store.</p>
                            )}
                            {readOnly && (
                                <InlineError message={header?.isGroup ? 'Only head office can change a Group PO.' : 'Only the PO\'s own store can change it.'} />
                            )}
                            <div className="multipo-dialog-grid">
                                {mode.kind === 'new' ? (
                                    <>
                                        <div className="form-field">
                                            <label className="form-label">Brand</label>
                                            <select className="form-input" value={brandId} disabled={brandsQuery.isLoading}
                                                onChange={e => { setBrandId(e.target.value); setCatalogGuid('') }}>
                                                <option value="">{brandsQuery.isLoading ? 'Loading brands…' : 'Choose a brand'}</option>
                                                {(brandsQuery.data ?? []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-field">
                                            <label className="form-label">Catalog</label>
                                            <select className="form-input" value={catalogGuid} disabled={!brandId || catalogsQuery.isLoading}
                                                onChange={e => setCatalogGuid(e.target.value)}>
                                                <option value="">{catalogsQuery.isLoading ? 'Loading catalogs…' : 'Choose a season'}</option>
                                                {(catalogsQuery.data ?? []).map(c => (
                                                    <option key={c.catalogGuid} value={c.catalogGuid}>{c.season ?? 'No season'} ({c.itemCount} items)</option>
                                                ))}
                                            </select>
                                        </div>
                                    </>
                                ) : (
                                    <div className="form-field multipo-dialog-wide">
                                        <label className="form-label">Catalog</label>
                                        <div className="multipo-dialog-static">{catalogLabel}</div>
                                    </div>
                                )}
                                <div className="form-field multipo-dialog-wide">
                                    <label className="form-label">PO number</label>
                                    <input className="form-input" autoFocus value={form.poNumber} maxLength={50} disabled={readOnly}
                                        onChange={set('poNumber')} />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Ship date</label>
                                    <input className="form-input" type="date" value={form.shipDate} disabled={readOnly} onChange={set('shipDate')} />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Lockdown date</label>
                                    <input className="form-input" type="date" value={form.lockdownDate} disabled={readOnly} onChange={set('lockdownDate')} />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Discount %</label>
                                    <input className="form-input" inputMode="numeric" value={form.discountPercent} disabled={readOnly} onChange={set('discountPercent')} />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Discount days</label>
                                    <input className="form-input" inputMode="numeric" value={form.discountDays} disabled={readOnly} onChange={set('discountDays')} />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Net days</label>
                                    <input className="form-input" inputMode="numeric" value={form.termsDays} disabled={readOnly} onChange={set('termsDays')} />
                                </div>
                                <div className="form-field">
                                    <label className="form-label">Shipping method</label>
                                    <select className="form-input" value={form.shippingMethod} disabled={readOnly} onChange={set('shippingMethod')}>
                                        {SHIPPING_METHODS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            {!readOnly && (error ?? (problem && form.poNumber.trim() ? problem : null)) && (
                                <InlineError message={(error ?? problem)!} style={{ marginTop: 'var(--space-3)' }} />
                            )}
                        </>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={submit}
                        disabled={loading || readOnly || busy || !!problem || !catalogGuid}>
                        {busy ? 'Saving…' : mode.kind === 'edit' ? 'Save' : 'Create PO'}
                    </button>
                </div>
            </div>
        </div>
    )
}

function errorText(err: unknown): string | null {
    if (!(err instanceof Error)) return null
    try {
        return JSON.parse(err.message)?.error ?? null
    } catch {
        return err.message || null
    }
}
